import React from 'react'
import { ILogStats } from '../scripts/calculateStats'
import '../styles/logsStats.css'

interface LogsStatsProps {
    stats: ILogStats
}

function LogsStats({ stats }: LogsStatsProps) {
    const StatCard = ({ title, value, unit = '' }: { title: string; value: string | number; unit?: string }) => (
        <div className='stat_card'>
            <div className='stat_label'>{title}</div>
            <div className='stat_value'>
                {value}
                {unit && <span className='stat_unit'>{unit}</span>}
            </div>
        </div>
    )

    return (
        <div className='logs_stats_container'>
            <div className='stats_section'>
                <h3 className='section_title'>Estatísticas Gerais</h3>
                <div className='stats_grid'>
                    <StatCard
                        title='Total de Requisições'
                        value={stats.totalRequests}
                    />
                    <StatCard
                        title='Taxa de Sucesso'
                        value={stats.successRate}
                        unit='%'
                    />
                    <StatCard
                        title='IPs Únicos'
                        value={stats.totalIPs}
                    />
                    <StatCard
                        title='Requisições com Sucesso'
                        value={stats.successCount}
                    />
                    <StatCard
                        title='Requisições com Erro'
                        value={stats.errorCount}
                    />
                </div>
            </div>

            <div className='stats_section'>
                <h3 className='section_title'>Tempo de Resposta (ms)</h3>
                <div className='stats_grid'>
                    <StatCard
                        title='Tempo Médio'
                        value={stats.averageResponseTime}
                        unit='ms'
                    />
                    <StatCard
                        title='Tempo Mais Rápido'
                        value={stats.fastestResponse}
                        unit='ms'
                    />
                    <StatCard
                        title='Tempo Mais Lento'
                        value={stats.slowestResponse}
                        unit='ms'
                    />
                </div>
            </div>

            {stats.topMethods.length > 0 && (
                <div className='stats_section'>
                    <h3 className='section_title'>Métodos HTTP Mais Usados</h3>
                    <div className='stats_list'>
                        {stats.topMethods.map((method, idx) => (
                            <div key={idx} className='list_item'>
                                <div className='item_label'>{method.method}</div>
                                <div className='item_bar'>
                                    <div
                                        className='bar_fill'
                                        style={{
                                            width: `${(method.count / stats.topMethods[0].count) * 100}%`
                                        }}
                                    ></div>
                                </div>
                                <div className='item_value'>{method.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {stats.topStatus.length > 0 && (
                <div className='stats_section'>
                    <h3 className='section_title'>Status HTTP Mais Frequentes</h3>
                    <div className='stats_list'>
                        {stats.topStatus.map((status, idx) => {
                            const isError = status.status.startsWith('4') || status.status.startsWith('5')
                            return (
                                <div key={idx} className='list_item'>
                                    <div className={`item_label ${isError ? 'error' : 'success'}`}>
                                        {status.status}
                                    </div>
                                    <div className='item_bar'>
                                        <div
                                            className={`bar_fill ${isError ? 'error' : 'success'}`}
                                            style={{
                                                width: `${(status.count / stats.topStatus[0].count) * 100}%`
                                            }}
                                        ></div>
                                    </div>
                                    <div className='item_value'>{status.count}</div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}

            {stats.topIPs.length > 0 && (
                <div className='stats_section'>
                    <h3 className='section_title'>Top 10 IPs com Mais Requisições</h3>
                    <div className='stats_list'>
                        {stats.topIPs.map((ip, idx) => (
                            <div key={idx} className='list_item'>
                                <div className='item_label ip_address'>{ip.ip}</div>
                                <div className='item_bar'>
                                    <div
                                        className='bar_fill'
                                        style={{
                                            width: `${(ip.count / stats.topIPs[0].count) * 100}%`
                                        }}
                                    ></div>
                                </div>
                                <div className='item_value'>{ip.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {stats.topRoutes.length > 0 && (
                <div className='stats_section'>
                    <h3 className='section_title'>Top 10 Rotas Mais Acessadas</h3>
                    <div className='stats_list'>
                        {stats.topRoutes.slice(0, 10).map((route, idx) => (
                            <div key={idx} className='list_item'>
                                <div className='item_label route_path'>{route.rota}</div>
                                <div className='item_bar'>
                                    <div
                                        className='bar_fill'
                                        style={{
                                            width: `${(route.count / stats.topRoutes[0].count) * 100}%`
                                        }}
                                    ></div>
                                </div>
                                <div className='item_value'>{route.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {stats.topClients.length > 0 && (
                <div className='stats_section'>
                    <h3 className='section_title'>Top 10 Clientes com Mais Requisições</h3>
                    <div className='stats_list'>
                        {stats.topClients.map((client, idx) => (
                            <div key={idx} className='list_item'>
                                <div className='item_label client_name'>{client.client}</div>
                                <div className='item_bar'>
                                    <div
                                        className='bar_fill'
                                        style={{
                                            width: `${(client.count / stats.topClients[0].count) * 100}%`
                                        }}
                                    ></div>
                                </div>
                                <div className='item_value'>{client.count}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    )
}

export default LogsStats
